/**
 * TapRipple — circle that expands and fades out where the player tapped.
 *
 * One-shot Graphics ring tweened to ~2.4× scale + alpha 0 over ~380ms,
 * then auto-destroyed. The same effect appears in horse-dash, puff-up,
 * tap-tug, fruit-grab, simon-says — extracted here so games can `import`
 * it instead of redefining `_spawnTapRipple` per scene.
 *
 * Usage:
 *   import { TapRipple } from '@platform/sdk';
 *
 *   this.input.on('pointerdown', (pointer) => {
 *     TapRipple.spawn(this, { x: pointer.x, y: pointer.y, color: 0x6d8aff });
 *   });
 *
 * Common variants:
 *   // Faint "you tapped at a bad time" hint
 *   TapRipple.spawn(this, { x, y, alpha: 0.4 });
 *
 *   // Strong team-colored confirm
 *   TapRipple.spawn(this, { x, y, color: TEAM_COLORS[team], alpha: 0.95 });
 */
export class TapRipple {
  /**
   * Spawn a single ripple at (x, y).
   * @param {Phaser.Scene} scene
   * @param {object} opts
   * @param {number} opts.x
   * @param {number} opts.y
   * @param {number} [opts.color=0xffffff]
   * @param {number} [opts.alpha=0.9]       initial line alpha
   * @param {number} [opts.radius=18]       starting radius (px)
   * @param {number} [opts.scaleTo=2.4]     final scale
   * @param {number} [opts.duration=380]    ms
   * @param {number} [opts.lineWidth=4]
   * @param {number} [opts.depth=14]
   */
  static spawn(scene, opts) {
    if (!scene || !opts) return null;
    const color = opts.color != null ? opts.color : 0xffffff;
    const alpha = opts.alpha != null ? opts.alpha : 0.9;
    const radius = opts.radius != null ? opts.radius : 18;
    const lineWidth = opts.lineWidth != null ? opts.lineWidth : 4;
    const depth = opts.depth != null ? opts.depth : 14;

    const ring = scene.add.graphics().setDepth(depth);
    ring.lineStyle(lineWidth, color, alpha);
    ring.strokeCircle(0, 0, radius);
    ring.x = opts.x;
    ring.y = opts.y;

    scene.tweens.add({
      targets: ring,
      scaleX: opts.scaleTo != null ? opts.scaleTo : 2.4,
      scaleY: opts.scaleTo != null ? opts.scaleTo : 2.4,
      alpha: 0,
      duration: opts.duration != null ? opts.duration : 380,
      ease: 'Cubic.out',
      onComplete: () => ring.destroy()
    });
    return ring;
  }
}
